// Bandeja de Emitidos. Financiero → Comprobantes Electrónicos.
// Responde en un solo lugar la pregunta que hoy exige abrir cinco pantallas:
// ¿qué comprobantes emití que NO están autorizados? Une facturas, refacturación,
// notas de crédito y débito (financiero.tickets, incluidas las ventas del POS),
// retenciones, liquidaciones de compra y guías de remisión.
//
// OJO: no es «Comprobantes Emitidos» (/sri/documentos-emitidos), que muestra lo
// DESCARGADO del portal. Esta muestra el estado de lo que generamos nosotros —
// justo lo que todavía no llegó al portal cuando algo falla.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, AlertTriangle, Clock, Inbox, ExternalLink, RefreshCw, FileText, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import { ExportarBotones, useMarcaEmpresa } from '@/lib/utils/exportarTabla';
import { SortableTh, useSort } from './tablaSort';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const usd = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const num = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const urlDoc = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

const SITUACION: Record<string, { label: string; cls: string }> = {
  problema:   { label: 'Con problema', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  en_curso:   { label: 'En curso',     cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  pendiente:  { label: 'Por enviar',   cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  autorizado: { label: 'Autorizado',   cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  anulado:    { label: 'Anulado',      cls: 'bg-muted text-muted-foreground' },
  otro:       { label: 'Otro',         cls: 'bg-muted text-muted-foreground' },
};
const CANAL: Record<string, string> = {
  POS: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  Refacturación: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
};
// Reenviar solo tiene sentido desde estos estados; coincide con lo que aceptan
// las RPC de cada tipo (fe_emitir, ret_emitir, liq_emitir).
const REINTENTABLE = ['por_emitir', 'rechazado', 'devuelto', 'error'];

interface Fila {
  fuente: string; id: string; tipo: string; cod_doc: string; canal: string;
  fecha_emision: string; serie_comprobante: string | null;
  contraparte: string | null; identificacion: string | null;
  importe_total: number; estado_emision: string; situacion: string;
  mensaje_sri: string | null; clave_acceso: string | null;
  xml_url: string | null; ride_url: string | null;
  horas_en_estado: number; emision_intentos: number | null; ruta: string;
}
interface Bandeja {
  desde: string; hasta: string;
  por_situacion: { situacion: string; documentos: number; total: number }[];
  por_tipo: { tipo: string; situacion: string; documentos: number }[];
  filas: Fila[]; total_filas: number; truncado: boolean;
  con_problema: number; atascados: number;
}

function rangos() {
  const f = (d: Date) => d.toISOString().slice(0, 10);
  const hoy = new Date();
  const atras = (d: number) => f(new Date(Date.now() - d * 86400000));
  return [
    { t: '30 días', d: atras(30), h: f(hoy) },
    { t: '90 días', d: atras(90), h: f(hoy) },
    { t: 'Año actual', d: `${hoy.getFullYear()}-01-01`, h: f(hoy) },
  ];
}

export function BandejaEmitidos() {
  const R = useMemo(rangos, []);
  const qc = useQueryClient();
  const openTab = useTabsStore((s) => s.openTab);
  const marca = useMarcaEmpresa();
  const [desde, setDesde] = useState(R[1].d);
  const [hasta, setHasta] = useState(R[1].h);
  const [situacion, setSituacion] = useState('atencion');
  const [tipo, setTipo] = useState('');

  const { data, isFetching } = useQuery<Bandeja>({
    queryKey: ['bandeja-emitidos', desde, hasta, situacion, tipo],
    enabled: !!desde && !!hasta && hasta >= desde,
    queryFn: async () => (await apiClient.post('/rpc/fin_emitidos_bandeja', {
      _desde: desde, _hasta: hasta,
      _situacion: situacion === 'todas' ? null : situacion,
      _tipo: tipo || null,
    }, FIN)).data,
    // mientras haya algo en curso conviene refrescar: el worker lo está moviendo
    refetchInterval: (q) => ((q.state.data as Bandeja | undefined)?.filas ?? []).some((f) => f.situacion === 'en_curso') ? 5000 : false,
  });

  const reintentar = useMutation({
    mutationFn: async (f: Fila) =>
      (await apiClient.post('/rpc/fin_emitidos_reintentar', { _fuente: f.fuente, _id: f.id }, FIN)).data,
    onSuccess: () => { toast.success('Encolado para reenviar al SRI'); void qc.invalidateQueries({ queryKey: ['bandeja-emitidos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const dlg = useDialogoEnLinea();

  const filas = data?.filas ?? [];
  const { sorted, sort, toggle } = useSort(filas);
  const tipos = useMemo(() => [...new Set((data?.por_tipo ?? []).map((t) => t.tipo))].sort(), [data]);
  const cuenta = (s: string) => (data?.por_situacion ?? []).find((x) => x.situacion === s)?.documentos ?? 0;

  const headers = ['Situación', 'Tipo', 'Cód.', 'Serie', 'Origen', 'Fecha', 'Contraparte', 'Identificación',
    'Total', 'Estado SRI', 'Horas en estado', 'Mensaje del SRI'];
  const filasExp = sorted.map((f) => [
    SITUACION[f.situacion]?.label ?? f.situacion, f.tipo, f.cod_doc, f.serie_comprobante ?? '', f.canal,
    String(f.fecha_emision).slice(0, 10), f.contraparte ?? '', f.identificacion ?? '',
    Number(f.importe_total), f.estado_emision, Number(f.horas_en_estado), f.mensaje_sri ?? '',
  ]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Inbox className="w-5 h-5" /> Bandeja de Emitidos</h1>
          <p className="text-sm text-muted-foreground">
            Todo lo que emitiste y su estado de autorización, en un solo lugar: facturas y ventas del POS,
            notas de crédito y débito, retenciones, liquidaciones y guías.
          </p>
        </div>
        <ExportarBotones
          nombre={`bandeja-emitidos-${desde}_${hasta}`}
          titulo={`Bandeja de emitidos · ${desde} a ${hasta}`}
          subtitulo={`${data?.total_filas ?? 0} comprobantes · ${data?.con_problema ?? 0} con problema`}
          headers={headers} filas={filasExp} colsDerecha={[8, 10]}
          disabled={filasExp.length === 0} marca={marca}
        />
      </div>

      {/* Lo que urge, calculado sobre todo el período: no depende del filtro activo */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card className={data && data.con_problema > 0 ? 'border-red-300 dark:border-red-900/50' : ''}>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Con problema</p>
            <p className={`text-2xl font-semibold tabular-nums ${data && data.con_problema > 0 ? 'text-red-600' : ''}`}>{data?.con_problema ?? 0}</p>
            <p className="text-xs text-muted-foreground">rechazados, devueltos o con error</p>
          </CardContent>
        </Card>
        <Card className={data && data.atascados > 0 ? 'border-amber-300 dark:border-amber-900/50' : ''}>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Atascados</p>
            <p className={`text-2xl font-semibold tabular-nums ${data && data.atascados > 0 ? 'text-amber-600' : ''}`}>{data?.atascados ?? 0}</p>
            <p className="text-xs text-muted-foreground">más de 2 h en curso — revisa el worker</p>
          </CardContent>
        </Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Por enviar</p>
          <p className="text-2xl font-semibold tabular-nums">{cuenta('pendiente')}</p>
          <p className="text-xs text-muted-foreground">borradores sin emitir</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Autorizados</p>
          <p className="text-2xl font-semibold tabular-nums text-green-600">{cuenta('autorizado')}</p>
          <p className="text-xs text-muted-foreground">{usd((data?.por_situacion ?? []).find((x) => x.situacion === 'autorizado')?.total ?? 0)}</p>
        </CardContent></Card>
      </div>

      {data && data.atascados > 0 && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
          <Clock className="w-4 h-4 shrink-0 mt-0.5" />
          Hay {data.atascados} comprobante(s) más de dos horas «en curso». Cuando el envío funciona esto tarda
          segundos: casi siempre significa que el worker de emisión está detenido.
          <button className="underline shrink-0"
            onClick={() => openTab({ id: '/sistemas/procesos', title: 'Monitor de procesos', path: '/sistemas/procesos' })}>
            Ver monitor de procesos
          </button>
        </p>
      )}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="flex flex-wrap gap-1 rounded-lg border p-1">
            {([['atencion', 'Requieren atención'], ['problema', 'Con problema'], ['en_curso', 'En curso'],
               ['pendiente', 'Por enviar'], ['autorizado', 'Autorizados'], ['todas', 'Todos']] as const).map(([k, t]) => (
              <button key={k} onClick={() => setSituacion(k)}
                className={`rounded-md px-3 py-1 text-sm ${situacion === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
            ))}
          </div>
          <label className="flex flex-col text-xs text-muted-foreground">Desde
            <input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
              value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
          <label className="flex flex-col text-xs text-muted-foreground">Hasta
            <input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
              value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
          <label className="flex flex-col text-xs text-muted-foreground">Tipo
            <select className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
              value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="">Todos</option>
              {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></label>
          <div className="flex gap-1">
            {R.map((r) => <Button key={r.t} size="sm" variant="outline" onClick={() => { setDesde(r.d); setHasta(r.h); }}>{r.t}</Button>)}
          </div>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Comprobantes ({data?.total_filas ?? 0})</CardTitle>
          {data?.truncado && (
            <p className="text-xs text-amber-600">
              Se muestran los primeros {filas.length}. Los contadores de arriba sí cubren el período completo.
            </p>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {filas.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {situacion === 'atencion'
                ? 'Nada pendiente de atención en este período. Todo lo emitido está autorizado.'
                : 'Sin comprobantes en este filtro.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2 pr-3 text-left w-10">N°</th>
                  <SortableTh col="situacion" label="Situación" sort={sort} onSort={toggle} />
                  <SortableTh col="tipo" label="Tipo" sort={sort} onSort={toggle} />
                  <SortableTh col="serie_comprobante" label="Serie" sort={sort} onSort={toggle} />
                  <SortableTh col="canal" label="Origen" sort={sort} onSort={toggle} />
                  <SortableTh col="fecha_emision" label="Fecha" sort={sort} onSort={toggle} />
                  <SortableTh col="contraparte" label="Contraparte" sort={sort} onSort={toggle} />
                  <SortableTh col="importe_total" label="Total" align="right" sort={sort} onSort={toggle} />
                  <SortableTh col="horas_en_estado" label="Horas" align="right" sort={sort} onSort={toggle} />
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((f, i) => (
                  <tr key={`${f.fuente}-${f.id}`} className="border-b last:border-0 hover:bg-muted/40 align-top">
                    <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${(SITUACION[f.situacion] || SITUACION.otro).cls}`}>
                        {(SITUACION[f.situacion] || SITUACION.otro).label}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5">{f.estado_emision}</span>
                    </td>
                    <td className="py-2 pr-3">
                      {f.tipo}
                      <span className="block text-xs text-muted-foreground">cód. {f.cod_doc}</span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{f.serie_comprobante || '—'}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${CANAL[f.canal] ?? 'bg-muted text-muted-foreground'}`}>{f.canal}</span>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">{String(f.fecha_emision).slice(0, 10)}</td>
                    <td className="py-2 pr-3">
                      {f.contraparte || '—'}
                      <span className="block text-xs text-muted-foreground">{f.identificacion}</span>
                      {f.mensaje_sri && f.situacion === 'problema' && (
                        <button type="button" onClick={() => void dlg.avisar('Mensaje del SRI', f.mensaje_sri!)}
                          className="mt-1 block max-w-md text-left text-xs text-red-600 hover:underline">
                          ⚠ {f.mensaje_sri.length > 120 ? f.mensaje_sri.slice(0, 120) + '…' : f.mensaje_sri}
                        </button>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{num(Number(f.importe_total))}</td>
                    <td className={`py-2 pr-3 text-right tabular-nums ${f.situacion === 'en_curso' && f.horas_en_estado > 2 ? 'text-amber-600 font-medium' : ''}`}>
                      {Number(f.horas_en_estado) < 24
                        ? `${Number(f.horas_en_estado).toFixed(1)} h`
                        : `${Math.round(Number(f.horas_en_estado) / 24)} d`}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {urlDoc(f.ride_url) && <a href={urlDoc(f.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><FileText className="w-4 h-4" /> RIDE</a>}
                      {urlDoc(f.xml_url) && <a href={urlDoc(f.xml_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><Download className="w-4 h-4" /> XML</a>}
                      <button className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"
                        title="Abrir la pantalla que lo gestiona"
                        onClick={() => openTab({ id: f.ruta, title: f.tipo, path: f.ruta })}>
                        <ExternalLink className="w-3.5 h-3.5" /> Abrir
                      </button>
                      {REINTENTABLE.includes(f.estado_emision) && (
                        <Button size="sm" variant="outline" isLoading={reintentar.isPending}
                          onClick={async () => {
                            if (await dlg.confirmar(`¿Reenviar al SRI ${f.tipo} ${f.serie_comprobante || '(sin serie)'}?`, { detalle: 'Se encola para que el worker lo firme y lo transmita.' })) reintentar.mutate(f);
                          }}>
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reenviar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t font-medium">
                <tr>
                  <td colSpan={7} className="py-2 pr-3 text-right text-muted-foreground">
                    {data?.truncado ? 'Total de lo mostrado' : 'Total'}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(filas.reduce((s, f) => s + Number(f.importe_total), 0))}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default BandejaEmitidos;
